import React, { useCallback, useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import { CssBaseline, CircularProgress, Box, Typography, Button } from '@mui/material';
import { MsalProvider } from '@azure/msal-react';
import { QueryClient, QueryClientProvider, useQueryClient } from '@tanstack/react-query';
import { SnackbarProvider } from 'notistack';
import { msalInstance, initializeMsal } from './services/auth/msalConfig';
import { theme } from './styles/theme';
import { Header } from './components/common/Header';
import { BottomNav } from './components/common/BottomNav';
import { OfflineIndicator } from './components/common/OfflineIndicator';
import { UpdatePrompt } from './components/common/UpdatePrompt';
import { ErrorBoundary } from './components/common/ErrorBoundary';
import { PullToRefresh } from './components/common/PullToRefresh';
import { GlobalPullToRefreshIndicator } from './components/common/GlobalPullToRefreshIndicator';
import { useGlobalPullToRefresh } from './hooks/useGlobalPullToRefresh';
import { prefetchMarketData } from './utils/prefetchMarketData';

// Pages
import { Home } from './pages/Home';
import { Continents } from './pages/Continents';
import { Countries } from './pages/Countries';
import { Markets } from './pages/Markets';
import { MarketDetail } from './pages/MarketDetail';
import { KPIDetail } from './pages/KPIDetail';
import { AirportDetail } from './pages/AirportDetail';
import { Favorites } from './pages/Favorites';
import { Settings } from './pages/Settings';
import { Search } from './pages/Search';
import { Chat } from './pages/Chat';
import { MiaKnowledgeBase } from './pages/MiaKnowledgeBase';

const LoginPage = () => {
  const [isLoading, setIsLoading] = useState(false);
  const account = msalInstance.getActiveAccount();

  // Reset loading state and clear interaction status when component mounts
  useEffect(() => {
    // Clear any stale interaction status from previous sessions
    Object.keys(localStorage).forEach(key => {
      if (key.includes('interaction.status')) {
        localStorage.removeItem(key);
      }
    });

    // Ensure loading state is reset
    setIsLoading(false);
  }, []);

  const handleLogin = async () => {
    if (isLoading) return; // Prevent double-clicks

    setIsLoading(true);
    try {
      // Clear any stale interaction status
      Object.keys(localStorage).forEach(key => {
        if (key.includes('interaction.status')) {
          localStorage.removeItem(key);
        }
      });

      // Small delay to ensure localStorage clear completes
      await new Promise(resolve => setTimeout(resolve, 100));

      await msalInstance.loginRedirect({
        scopes: ['User.Read', 'email', 'profile'],
        prompt: 'select_account',
      });
    } catch (error: any) {
      console.error('Login failed:', error);
      // Clear interaction status on error and allow retry
      Object.keys(localStorage).forEach(key => {
        if (key.includes('interaction.status')) {
          localStorage.removeItem(key);
        }
      });
      setIsLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await msalInstance.logoutRedirect({
        postLogoutRedirectUri: window.location.origin,
      });
    } catch (error: any) {
      console.error('Logout failed:', error);
    }
  };

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      minHeight="100vh"
      p={3}
      sx={{
        background: 'linear-gradient(135deg, #003F9F 0%, #607D8B 100%)',
      }}
    >
      <Box
        sx={{
          background: 'white',
          borderRadius: 2,
          p: 4,
          maxWidth: 400,
          width: '100%',
          textAlign: 'center',
        }}
      >
        <Box
          component="img"
          src="/images/horwath_mountain_transparent.png"
          alt="Horwath HTL"
          sx={{
            width: '200px',
            height: 'auto',
            mx: 'auto',
            mb: 3,
            display: 'block',
          }}
        />
        <Typography variant="h4" gutterBottom fontWeight="bold" color="primary">
          HTL Market Insights
        </Typography>
        <Typography variant="body1" color="text.secondary" mb={3}>
          Access market analytics and KPIs on the go
        </Typography>
        <Button
          variant="contained"
          size="large"
          fullWidth
          onClick={handleLogin}
          disabled={isLoading}
        >
          {isLoading ? 'Signing in...' : 'Sign in with Microsoft'}
        </Button>

        {account && (
          <>
            <Typography variant="body2" color="error.main" mt={2}>
              Access restricted. Signed in as: {account.username}
            </Typography>
            <Button
              variant="outlined"
              size="medium"
              fullWidth
              onClick={handleLogout}
              sx={{ mt: 1 }}
            >
              Sign out and try again
            </Button>
          </>
        )}

        <Typography variant="caption" color="text.secondary" display="block" mt={3}>
          Restricted to @horwathhtl.com accounts
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block" mt={1}>
          Mobile PWA - Version 1.0.0
        </Typography>
      </Box>
    </Box>
  );
};

const LoadingScreen = () => (
  <Box
    display="flex"
    flexDirection="column"
    alignItems="center"
    justifyContent="center"
    minHeight="100vh"
  >
    <CircularProgress size={48} />
    <Typography variant="body2" mt={2}>
      Loading...
    </Typography>
  </Box>
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 30 * 60 * 1000, // 30 minutes
      retry: (failureCount) => {
        if (!navigator.onLine) return false;
        return failureCount < 3;
      },
    },
  },
});

const AppLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const queryClient = useQueryClient();
  const location = useLocation();
  const isChatPage = location.pathname === '/chat';

  // Memoize to prevent unnecessary effect re-runs in PullToRefresh
  const handleRefresh = useCallback(async () => {
    // Invalidate all queries to trigger a fresh fetch
    await queryClient.invalidateQueries();
    // Small delay to ensure the data is refreshed
    await new Promise(resolve => setTimeout(resolve, 500));
  }, [queryClient]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100vh',
        overflow: 'hidden',
        background: 'linear-gradient(135deg, #FFFFFF 0%, #F8F9FA 100%)',
        backgroundAttachment: 'fixed',
        backgroundSize: '100vw 100vh',
        backgroundRepeat: 'no-repeat',
      }}
    >
      {/* Hide Header on chat page - chat will manage its own full-screen layout */}
      {!isChatPage && <Header />}
      <Box
        sx={{
          flex: 1,
          overflowY: isChatPage ? 'hidden' : 'auto', // No scroll on chat page
          overflowX: 'hidden',
        }}
      >
        {isChatPage ? (
          // Chat page handles its own layout, no PullToRefresh wrapper
          children
        ) : (
          <PullToRefresh onRefresh={handleRefresh}>
            {children}
          </PullToRefresh>
        )}
      </Box>
      <BottomNav />
    </Box>
  );
};

export const App: React.FC = () => {
  const [isReady, setIsReady] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Global pull-to-refresh for hard reload (fixes blank page after deployments)
  // Only triggers when:
  // 1. Touch starts within 100px of the top of the screen
  // 2. User pulls down at least 1/3 of the screen height
  const globalPullToRefreshState = useGlobalPullToRefresh({
    maxStartY: 100, // Touch must start within 100px of top of screen
    // threshold and maxPullDistance default to 1/3 of screen height
    onRefresh: async () => {
      // Clear service worker registrations before reload
      if ('serviceWorker' in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        await Promise.all(registrations.map(reg => reg.unregister()));
      }
    },
  });

  useEffect(() => {
    const initialize = async () => {
      try {
        await initializeMsal();
        const account = msalInstance.getActiveAccount();

        if (account) {
          // Validate domain
          const email = account.username?.toLowerCase();
          if (email && (email.endsWith('@horwathhtl.com') || email.endsWith('@barretthtl.com'))) {
            setIsAuthenticated(true);
          }
        }
      } catch (error) {
        console.error('Initialization error:', error);
      } finally {
        setIsReady(true);
      }
    };

    initialize();
  }, []);

  // Prefetch market data (continents and countries) after authentication succeeds
  useEffect(() => {
    if (isAuthenticated) {
      prefetchMarketData(queryClient).catch((error) => {
        console.error('Failed to prefetch market data:', error);
      });
    }
  }, [isAuthenticated]);

  if (!isReady) {
    return <LoadingScreen />;
  }

  return (
    <ErrorBoundary>
      <MsalProvider instance={msalInstance}>
        <QueryClientProvider client={queryClient}>
          <ThemeProvider theme={theme}>
            <CssBaseline />
            <SnackbarProvider
              maxSnack={3}
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'center',
              }}
            >
              <BrowserRouter>
                <OfflineIndicator />
                <UpdatePrompt />
                <GlobalPullToRefreshIndicator
                  isPulling={globalPullToRefreshState.isPulling}
                  pullDistance={globalPullToRefreshState.pullDistance}
                  isRefreshing={globalPullToRefreshState.isRefreshing}
                />
                {!isAuthenticated ? (
                  <LoginPage />
                ) : (
                  <AppLayout>
                    <Routes>
                      <Route path="/" element={<Home />} />
                      <Route path="/continents" element={<Continents />} />
                      <Route path="/countries/:continentId" element={<Countries />} />
                      <Route path="/markets/:countryId" element={<Markets />} />
                      <Route path="/market/:marketId" element={<MarketDetail />} />
                      <Route path="/market/:marketId/kpi/:kpiType" element={<KPIDetail />} />
                      <Route path="/market/:marketId/airport/:airportCode" element={<AirportDetail />} />
                      <Route path="/favorites" element={<Favorites />} />
                      <Route path="/search" element={<Search />} />
                      <Route path="/settings" element={<Settings />} />
                      <Route path="/chat" element={<Chat />} />
                      <Route path="/mia-knowledge" element={<MiaKnowledgeBase />} />
                      <Route path="*" element={<Navigate to="/" replace />} />
                    </Routes>
                  </AppLayout>
                )}
              </BrowserRouter>
            </SnackbarProvider>
          </ThemeProvider>
        </QueryClientProvider>
      </MsalProvider>
    </ErrorBoundary>
  );
};
